// Login (design_handoff_login §3): Felder, Knöpfe, Fehlerbalken, Theme-Schalter, Branding und Kartenrahmen.
import { useState, type ReactNode } from 'react';
import { CheckCircle, CircleNotch, Eye, EyeSlash, Moon, ShieldCheck, Sun, WarningCircle, type Icon } from '@phosphor-icons/react';
import { useImmoTheme } from '../../lib/immobilien/theme';

/** Eingabefeld 54 px mit Icon links; Fokus = Akzent-Ring 1.5 px + 4 px weich, Icon wird Akzentfarbe. Schrift 16 px (kein iOS-Zoom). */
export function LoginFeld({ label, icon: I, typ, wert, onAendern, platzhalter, autoComplete, rechts }: {
  label: string; icon: Icon; typ: string; wert: string; onAendern: (v: string) => void; platzhalter: string; autoComplete: string; rechts?: ReactNode;
}) {
  const [fokus, setFokus] = useState(false);
  return (
    <label className="flex flex-col" style={{ gap: 8 }}>
      <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink2)', paddingLeft: 4 }}>{label}</span>
      <span className="flex items-center"
        style={{ gap: 11, height: 54, padding: rechts ? '0 7px 0 16px' : '0 16px', borderRadius: 18, background: 'var(--fill)', transition: 'box-shadow .2s',
          boxShadow: fokus ? 'inset 0 0 0 1.5px var(--acc), 0 0 0 4px var(--acc-soft)' : 'inset 0 0 0 1px var(--line)' }}>
        <I size={19} weight="bold" color={fokus ? 'var(--acc)' : 'var(--ink3)'} style={{ transition: 'color .2s', flexShrink: 0 }} />
        <input type={typ} required value={wert} onChange={(e) => onAendern(e.target.value)} placeholder={platzhalter} autoComplete={autoComplete}
          onFocus={() => setFokus(true)} onBlur={() => setFokus(false)}
          className="min-w-0 flex-1" style={{ border: 'none', outline: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 16, fontWeight: 600, color: 'var(--ink)' }} />
        {rechts}
      </span>
    </label>
  );
}

/** Auge-Knopf für die Passwort-Sichtbarkeit. */
export function AugeKnopf({ sichtbar, onUmschalten, mobil = false }: { sichtbar: boolean; onUmschalten: () => void; mobil?: boolean }) {
  return (
    <button type="button" onClick={onUmschalten} aria-label={sichtbar ? 'Passwort verbergen' : 'Passwort anzeigen'} aria-pressed={sichtbar}
      data-tip={sichtbar ? 'Verbergen – Passwort ausblenden' : 'Anzeigen – Passwort im Klartext zeigen'}
      className="flex shrink-0 items-center justify-center hover:bg-[var(--fill2)]"
      style={{ width: mobil ? 44 : 42, height: mobil ? 44 : 42, border: 'none', borderRadius: '50%', cursor: 'pointer', background: 'transparent', color: 'var(--ink3)' }}>
      {sichtbar ? <EyeSlash size={19} weight="bold" /> : <Eye size={19} weight="bold" />}
    </button>
  );
}

/** Primär-Knopf 54 px (Pille, Akzent-Füllung); beim Laden Spinner und Opazität 0.7. */
export function PrimaerKnopf({ icon: I, laedt, tip, children }: { icon: Icon; laedt: boolean; tip: string; children: ReactNode }) {
  return (
    <button type="submit" disabled={laedt} data-tip={tip}
      className="flex items-center justify-center transition-transform duration-200 hover:-translate-y-px active:scale-[.98]"
      style={{ height: 54, gap: 10, border: 'none', borderRadius: 27, fontFamily: 'inherit', fontSize: 16, fontWeight: 800,
        cursor: laedt ? 'wait' : 'pointer', opacity: laedt ? 0.7 : 1, background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)' }}>
      {laedt ? <CircleNotch size={18} weight="bold" className="animate-spin" /> : <I size={18} weight="bold" />}
      {children}
    </button>
  );
}

/** Sekundär-Knopf 44 px (--fill). */
export function SekundaerKnopf({ icon: I, tip, onClick, children }: { icon: Icon; tip: string; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} data-tip={tip}
      className="flex items-center justify-center hover:!bg-[var(--fill2)] hover:!text-[var(--ink)]"
      style={{ height: 44, gap: 7, border: 'none', borderRadius: 22, cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 700, background: 'var(--fill)', color: 'var(--ink2)' }}>
      <I size={15} weight="bold" />{children}
    </button>
  );
}

export function FehlerBalken({ text }: { text: string }) {
  return (
    <div role="alert" className="login-slide flex items-start"
      style={{ gap: 10, padding: '12px 14px', borderRadius: 18, background: 'var(--bad-soft)', color: 'var(--bad)', fontSize: 13.5, fontWeight: 700, lineHeight: 1.4 }}>
      <WarningCircle size={19} weight="fill" className="shrink-0" style={{ marginTop: -1 }} />
      <span>{text}</span>
    </div>
  );
}

/** Erfolgs-Zustand (E-Mail gesendet / Passwort geändert). */
export function ErfolgsBlock({ icon: I, titel, children }: { icon: Icon; titel: string; children: ReactNode }) {
  return (
    <div className="flex flex-col items-center text-center" style={{ gap: 12, padding: '6px 0 4px' }}>
      <span className="login-rise flex items-center justify-center" style={{ width: 64, height: 64, borderRadius: 20, background: 'var(--ok-soft)', color: 'var(--ok)' }}>
        <I size={32} weight="fill" />
      </span>
      <span role="status" style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.02em' }}>{titel}</span>
      <span style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--ink2)' }}>{children}</span>
    </div>
  );
}

export const ErfolgIcon = CheckCircle;

/** Hell/Dunkel-Schalter oben rechts (vorhandener Theme-Mechanismus, Schlüssel immo-theme). */
export function ThemeSchalter({ mobil = false }: { mobil?: boolean }) {
  const [theme, setTheme] = useImmoTheme();
  const knopf = (wert: 'light' | 'dark', I: Icon, label: string, tip: string) => {
    const an = theme === wert;
    return (
      <button type="button" onClick={() => setTheme(wert)} aria-label={label} aria-pressed={an} data-tip={tip}
        className="flex items-center justify-center"
        style={{ width: mobil ? 44 : 36, height: mobil ? 44 : 30, border: 'none', borderRadius: mobil ? 13 : 11, cursor: 'pointer',
          background: an ? 'var(--acc-fill)' : 'transparent', color: an ? 'var(--acc-ink)' : 'var(--ink2)' }}>
        <I size={15} weight="fill" />
      </button>
    );
  };
  return (
    <div role="group" aria-label="Darstellung" className="immo-glas flex" style={{ gap: 3, padding: 3, borderRadius: 14 }}>
      {knopf('light', Sun, 'Hell', 'Hell – Helles Erscheinungsbild')}
      {knopf('dark', Moon, 'Dunkel', 'Dunkel – Dunkles Erscheinungsbild')}
    </div>
  );
}

/** App-Icon mit weichem Farb-Schein, darunter Name und Untertitel. */
export function Branding() {
  return (
    <div className="flex flex-col items-center" style={{ gap: 14 }}>
      <div className="relative">
        <span aria-hidden className="absolute" style={{ inset: -18, borderRadius: '50%', background: 'radial-gradient(closest-side, var(--acc-soft), transparent)', filter: 'blur(8px)' }} />
        <img src="/app-icon-192.png" alt="ImmoSchwede" className="relative" width={76} height={76}
          style={{ width: 76, height: 76, borderRadius: 20, boxShadow: '0 20px 40px -18px rgba(15,27,45,0.5), inset 0 0 0 1px var(--line)' }} />
      </div>
      <div className="flex flex-col items-center" style={{ gap: 2 }}>
        <span style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.03em' }}>ImmoSchwede</span>
        <span style={{ fontSize: 13.5, color: 'var(--ink3)' }}>Immobilienmanagement</span>
      </div>
    </div>
  );
}

/** Kartenkopf: Titel + Badge „Sicherheits-Portal“. */
export function KartenKopf({ titel }: { titel: string }) {
  return (
    <div className="flex flex-col items-center" style={{ gap: 10, marginBottom: 6 }}>
      <h1 className="m-0 text-center" style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.03em' }}>{titel}</h1>
      <span data-tip="Sicherheits-Portal – Zugang nur für freigeschaltete Benutzer" className="inline-flex items-center"
        style={{ height: 26, padding: '0 11px', gap: 6, borderRadius: 13, fontSize: 12, fontWeight: 800, background: 'var(--acc-soft)', color: 'var(--acc)' }}>
        <ShieldCheck size={14} weight="fill" />Sicherheits-Portal
      </span>
    </div>
  );
}

export function SslHinweis() {
  return (
    <div className="flex justify-center" style={{ paddingTop: 6 }}>
      <span data-tip="SSL-Geschützt – Verbindung ist verschlüsselt" className="inline-flex items-center"
        style={{ height: 26, padding: '0 11px', gap: 7, borderRadius: 13, fontSize: 12, fontWeight: 700, background: 'var(--fill)', color: 'var(--ink3)' }}>
        <span className="login-puls" style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--ok)', boxShadow: '0 0 0 3px var(--ok-soft)' }} />
        SSL-Geschützt
      </span>
    </div>
  );
}
